'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LuMenu, LuX } from 'react-icons/lu';
import { MdOutlineKeyboardArrowRight } from 'react-icons/md';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { fetchCategories } from '@eliadmin/core/src/api/products/product-api';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetPortal,
  SheetOverlay,
} from '@eliadmin/core/src/components/ui/sheet';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

interface DesktopSidebarProps {
  isHomepage?: boolean;
}

interface CategoryItemProps {
  category: CategoriesEntity;
  onClickLink: () => void;
  onOpenChildSheet: (category: CategoriesEntity) => void;
  isSelected?: boolean;
  level: 1 | 2 | 3;
}

const CategoryItem: React.FC<CategoryItemProps> = ({
  category,
  onClickLink,
  onOpenChildSheet,
  isSelected = false,
  level,
}) => {
  const { t } = useTranslations();

  return (
    <div
      className={cn(
        'flex min-h-16 w-full max-w-[280px] items-center justify-between rounded-[16px] px-2 xl:max-w-[350px]',
        isSelected && 'bg-[#FAFAFA] text-primary',
      )}
    >
      <Link
        onClick={onClickLink}
        href={ROUTES.CATEGORY({ slug: category.slug })}
        prefetchOnHover
        className="flex h-16 items-center justify-start gap-2"
      >
        {category.image && (
          <Image
            src={category.image.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(category?.image, category?.name)}
            width={96}
            height={96}
            quality={100}
            className="max-h-12 min-h-12 min-w-12 max-w-12 overflow-hidden rounded-full object-cover"
            sizes="96px"
          />
        )}
        <p className="line-clamp-1 overflow-ellipsis font-medium">{category.name}</p>
      </Link>
      {category.childs && category.childs.length > 0 && (
        <SheetTrigger
          className={cn(
            'flex max-h-6 min-h-6 min-w-6 max-w-6 items-center justify-center rounded-full bg-[#FAFAFA]',
          )}
          onClick={() => onOpenChildSheet(category)}
          aria-label={t('text2310')}
        >
          <MdOutlineKeyboardArrowRight size={14} className="text-black" />
        </SheetTrigger>
      )}
    </div>
  );
};

export function DesktopSidebar({ isHomepage }: DesktopSidebarProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const { isOpen, setIsOpen } = useDisclosure();
  const [isOpenLevel2, setIsOpenLevel2] = useState(false);
  const [isOpenLevel3, setIsOpenLevel3] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CategoriesEntity | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<CategoriesEntity | null>(null);
  const [hasMenuIntent, setHasMenuIntent] = useState(false);

  const { t } = useTranslations();

  const { data: parentCategories = { data: [] }, isLoading } = useQuery({
    queryKey: ['PARENT_CATEGORIES', locale],
    // This component also mounts inside a CSS-hidden desktop header on mobile.
    // The full tree is only useful once the menu is about to be opened.
    enabled: !!locale && (isOpen || hasMenuIntent),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: () =>
      fetchCategories({
        locale,
        query: {
          childs: true,
          page: 1,
          results: 1000,
          where: { parent: { exists: false } },
          image: true,
        },
      }),
  });

  const handleCloseAll = useCallback(() => {
    setIsOpenLevel3(false);
    setIsOpenLevel2(false);
    setIsOpen(false);
    setSelectedCategory(null);
    setSelectedSubCategory(null);
  }, []);

  const renderSheetHeader = (title: string) => (
    <SheetHeader className="flex w-full items-center justify-between">
      <SheetTitle className="text-xl font-medium tracking-tight text-[#636363] xl:text-3xl">
        {title}
      </SheetTitle>
      <SheetClose className="flex items-center justify-center" aria-label={t('text2311')}>
        <LuX size={24} className="text-black" />
      </SheetClose>
    </SheetHeader>
  );

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant={isHomepage ? 'secondary' : 'default'}
          aria-label={t('text2309')}
          onPointerEnter={() => setHasMenuIntent(true)}
          onFocus={() => setHasMenuIntent(true)}
          onPointerDown={() => setHasMenuIntent(true)}
        >
          <LuMenu
            className={cn('h-5 w-5 lg:h-6 lg:w-6', isHomepage ? 'text-black' : 'text-white')}
          />
          <p
            className={cn(
              'hidden text-sm md:block lg:text-base',
              isHomepage ? 'text-black' : 'text-white',
            )}
          >
            {t('text2309')}
          </p>
        </Button>
      </SheetTrigger>
      <SheetPortal>
        <SheetOverlay className={cn('bg-transparent backdrop-blur-none')} />
        <SheetContent
          closeIcon={{ wrapper: 'hidden' }}
          side="left"
          className={cn(
            'z-[55] flex w-[360px] flex-col items-start justify-start gap-6 border-none p-6 shadow-none xl:w-[430px]',
          )}
        >
          {renderSheetHeader(t('text2309'))}
          <div
            className={cn(
              'flex w-full flex-col overflow-y-auto',
              'ea-desktop-sidebar__categories-wrapper',
            )}
          >
            {isLoading ? (
              <p className="text-center text-gray-500">{t('text2312')}</p>
            ) : (
              parentCategories.data.map(category => (
                <Sheet
                  key={category.slug}
                  open={isOpenLevel2 && selectedCategory?.slug === category.slug}
                  onOpenChange={open => {
                    setIsOpenLevel2(open);
                    setSelectedCategory(open ? category : null);
                    if (!open) {
                      setIsOpenLevel3(false);
                      setSelectedSubCategory(null);
                    }
                  }}
                >
                  <CategoryItem
                    category={category}
                    onClickLink={handleCloseAll}
                    onOpenChildSheet={setSelectedCategory}
                    level={1}
                  />
                  <SheetContent
                    closeIcon={{ wrapper: 'hidden' }}
                    side="left"
                    className="z-[54] flex w-full max-w-[360px] translate-x-[360px] flex-col items-start justify-start gap-4 border-none p-6 shadow-none xl:max-w-[430px] xl:translate-x-[430px]"
                  >
                    {renderSheetHeader(selectedCategory?.name || t('text2309'))}
                    <div className={cn('flex w-full flex-col overflow-y-auto')}>
                      {selectedCategory?.childs?.map(child => (
                        <Sheet
                          key={child.slug}
                          open={isOpenLevel3 && selectedSubCategory?.slug === child.slug}
                          onOpenChange={open => {
                            setIsOpenLevel3(open);
                            setSelectedSubCategory(open ? child : null);
                          }}
                        >
                          <CategoryItem
                            category={child}
                            onClickLink={handleCloseAll}
                            onOpenChildSheet={setSelectedSubCategory}
                            isSelected={selectedSubCategory?.slug === child.slug}
                            level={2}
                          />
                          <SheetContent
                            closeIcon={{ wrapper: 'hidden' }}
                            side="left"
                            className="z-[53] flex w-full max-w-[360px] translate-x-[720px] flex-col items-start justify-start gap-4 border-none p-6 shadow-none xl:max-w-[430px] xl:translate-x-[860px]"
                          >
                            {renderSheetHeader(selectedSubCategory?.name || t('text2309'))}
                            <div className={cn('flex w-full flex-col overflow-y-auto')}>
                              {selectedSubCategory?.childs?.map(grandchild => (
                                <CategoryItem
                                  key={grandchild.slug}
                                  category={grandchild}
                                  onClickLink={handleCloseAll}
                                  onOpenChildSheet={() => {}}
                                  level={3}
                                />
                              ))}
                            </div>
                          </SheetContent>
                        </Sheet>
                      ))}
                    </div>
                  </SheetContent>
                </Sheet>
              ))
            )}
          </div>
        </SheetContent>
      </SheetPortal>
    </Sheet>
  );
}
